import { recordActivity } from '../../../services/engagement';
import { useState, useEffect } from "react";
import { api } from "../../lib/api";
import { useTenant } from "../../hooks/useTenant";
import {
  executiveSummary, nextSteps, findingsByCategory, glanceByCategory,
  reportFooter, contactLine,
} from "../../lib/deliverable";
import { buildReport, exportPdf, exportPptx } from "../../lib/deliverableExport";

// Everything the client deliverable shows, plus exporting it.
//
// Owned by the assessment editor rather than the report view, so the export is one click
// from anywhere in the editor — the top bar's Export menu and the nav's Export group —
// not only from inside Preview. The preview and the full-screen presentation render from
// the same object, so what you see is what the files contain.

const REPORT_PRIMARY = "#093070";  // the kit's seeded --report-primary
const REPORT_ACCENT = "#6fca42";   // and --report-accent

const MONTH = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];

export const longDate = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v).slice(0, 10);
  return `${d.getDate()} ${MONTH[d.getMonth()]} ${d.getFullYear()}`;
};
export const monthOf = (v) => {
  if (!v) return "";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "" : MONTH[d.getMonth()];
};

export function useDeliverable({ assessment, categories, items, counts, score, me, siblings = [] }) {
  const [progress, setProgress] = useState(null);
  const [plan, setPlan] = useState("");
  const [exporting, setExporting] = useState("");
  const [exportError, setExportError] = useState("");
  const [exported, setExported] = useState("");
  // Website, contact details and the footer wording live on the tenant record, not on
  // `me`, so the deliverable reads them rather than inventing a contact line.
  const { tenant: brandRecord } = useTenant();

  const assessmentId = assessment?.id;
  const assessmentDate = assessment?.assessment_date;

  // The previous review for this client, if there is one. Everything in the "Progress
  // since the last review" band comes from comparing against it — nothing here is
  // written by hand, so a first review simply has no band rather than a fabricated one.
  const previous = [...siblings]
    .filter((s) => String(s.assessment_date) < String(assessmentDate))
    .sort((a, b) => String(b.assessment_date).localeCompare(String(a.assessment_date)))[0];
  const previousId = previous?.id;

  useEffect(() => {
    if (!previousId || !assessmentId) { setProgress(null); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get(`s4/api/assessments/${previousId}/compare?to=${assessmentId}`);
        if (!cancelled) setProgress(data);
      } catch { /* no comparison band rather than a wrong one */ }
    })();
    return () => { cancelled = true; };
  }, [previousId, assessmentId]);

  useEffect(() => {
    if (!assessmentId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get(`s4/api/assessments/${assessmentId}/narrative`);
        if (!cancelled) setPlan(data?.agreed_plan || "");
      } catch { /* the section simply does not appear */ }
    })();
    return () => { cancelled = true; };
  }, [assessmentId]);

  const brand = Array.isArray(me?.brand_colors) ? me.brand_colors : [];
  const primary = brand[0] || REPORT_PRIMARY;
  const accent = brand[1] || REPORT_ACCENT;
  const tenant = me?.tenant_name || "Your workspace";

  // Everything below is derived, not stored — see lib/deliverable.js.
  const summary = executiveSummary(items, score);
  const steps = nextSteps(items);
  const findings = findingsByCategory(categories, items);
  const glance = glanceByCategory(categories, items);
  const delta = progress?.point_change;

  const orgName = assessment?.organization_name;
  const footerLine = reportFooter(brandRecord?.report_tagline, orgName, tenant);
  const contact = contactLine(brandRecord);
  const logo = me?.tenant_large_logo || me?.tenant_logo;
  const previousMonth = monthOf(previous?.assessment_date);

  const coverTitle = progress ? "What changed this quarter." : "Where your technology stands.";
  const coverLede = progress
    ? `Where your technology stands today, what moved since ${previousMonth}, and what is worth doing next.`
    : "Where your technology stands today, and what is worth doing next.";

  // Both files are built from this one object, from the same values the page renders.
  const exportAs = async (kind) => {
    setExporting(kind);
    setExportError("");
    setExported("");
    try {
      const report = buildReport({
        assessment, score, counts, tenant, me, logo, primary, accent,
        summary, glance, findings, steps, plan, progress, previousMonth,
        footerLine, contact,
        title: coverTitle, lede: coverLede, preparedDate: longDate(assessmentDate),
      });
      await (kind === "pdf" ? exportPdf(report) : exportPptx(report)); recordActivity('reviews.browser_exported');
      setExported(kind);
    } catch (e) {
      console.error("[deliverable] export failed", e);
      setExportError(`The ${kind === "pdf" ? "PDF" : "PowerPoint"} could not be created. Please try again.`);
    } finally {
      setExporting("");
    }
  };

  return {
    previous, progress, plan, primary, accent, tenant, summary, steps, findings, glance, delta,
    orgName, footerLine, contact, logo, coverTitle, coverLede,
    exportAs, exporting, exportError, exported,
    clearExportStatus: () => { setExportError(""); setExported(""); },
  };
}
